import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { POSITIONS, PAY_PACKAGES, IMAGES } from "@/lib/data";
import { SectionHeading, Overline } from "@/components/Section";
import { CtaBanner } from "@/pages/Home";
import SEOHead from "@/components/SEOHead";
import LocalPositionsMap from "@/components/LocalPositionsMap";

const LOGO_IMG = "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/4723859dfc697053410c88fca6f1c41af1f0aa1c43fb8f500fdcd4f43fe0f00e.jpeg";

const POSITIONS_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "JobPosting",
      "title": "OTR CDL-A Company Truck Driver",
      "description": "Company OTR truck driver position. Earn 80¢–86¢ CPM or $1 CPM team driving. Dry van, flatbed, and reefer available. New trucks, weekly pay, flexible home time. All 48 states. No application fee.",
      "datePosted": "2026-02-01",
      "validThrough": "2026-12-31",
      "employmentType": "FULL_TIME",
      "directApply": true,
      "hiringOrganization": {
        "@type": "Organization",
        "name": "Hylas Companion Trucking",
        "sameAs": "https://hylascompanion.com",
        "logo": LOGO_IMG,
      },
      "jobLocation": {
        "@type": "Place",
        "address": { "@type": "PostalAddress", "addressCountry": "US" },
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": { "@type": "QuantitativeValue", "minValue": 41600, "maxValue": 112320, "unitText": "YEAR" },
      },
      "qualifications": "Valid CDL-A license. OTR experience preferred. Clean MVR. DOT medical card.",
      "responsibilities": "Haul dry van, flatbed, or reefer freight nationwide across all 48 states. Maintain ELD logs, pre/post trip inspections.",
      "jobBenefits": "Weekly pay, health insurance, new trucks, paid orientation, referral bonus",
      "applicantLocationRequirements": { "@type": "Country", "name": "United States" },
    },
    {
      "@type": "JobPosting",
      "title": "Lease Purchase CDL-A Truck Driver",
      "description": "Lease purchase truck driver — keep 88% of all revenue. New trucks available. Flatbed $3.40/mi, Reefer $3.20/mi, Dry Van $3.00/mi average gross. Weekly settlements, real path to ownership.",
      "datePosted": "2026-02-01",
      "validThrough": "2026-12-31",
      "employmentType": "CONTRACTOR",
      "directApply": true,
      "hiringOrganization": {
        "@type": "Organization",
        "name": "Hylas Companion Trucking",
        "sameAs": "https://hylascompanion.com",
        "logo": LOGO_IMG,
      },
      "jobLocation": {
        "@type": "Place",
        "address": { "@type": "PostalAddress", "addressCountry": "US" },
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": { "@type": "QuantitativeValue", "minValue": 55000, "maxValue": 130000, "unitText": "YEAR" },
      },
      "qualifications": "Valid CDL-A. 1+ year OTR experience preferred. Clean driving record.",
      "applicantLocationRequirements": { "@type": "Country", "name": "United States" },
    },
    {
      "@type": "JobPosting",
      "title": "Local / Regional CDL-A Truck Driver — W2 Weekly Pay",
      "description": "Local and regional truck driver positions. Earn $1,200–$2,500/week W2 or $30/hr hourly. Home daily or weekly. Steady dedicated freight across 22+ markets nationwide.",
      "datePosted": "2026-02-01",
      "validThrough": "2026-12-31",
      "employmentType": "FULL_TIME",
      "directApply": true,
      "hiringOrganization": {
        "@type": "Organization",
        "name": "Hylas Companion Trucking",
        "sameAs": "https://hylascompanion.com",
        "logo": LOGO_IMG,
      },
      "jobLocation": {
        "@type": "Place",
        "address": { "@type": "PostalAddress", "addressLocality": "Chicago", "addressRegion": "IL", "addressCountry": "US" },
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": { "@type": "QuantitativeValue", "minValue": 62400, "maxValue": 130000, "unitText": "YEAR" },
      },
      "jobBenefits": "Home daily or weekly, weekly W2 pay, health insurance, paid holidays",
      "qualifications": "Valid CDL-A. Local or OTR experience. Clean MVR.",
      "applicantLocationRequirements": { "@type": "Country", "name": "United States" },
    },
  ],
};

// Map position keys to their relevant packages
const POSITION_PACKAGES = {
  "company-driver": ["team", "company-cpm", "company-ppl"],
  "lease-rent": ["lease", "rent"],
  "local-regional": ["local-w2", "local-hr"],
  "entry-level": ["trainee", "boxtruck"],
};

export default function Positions() {
  return (
    <div data-testid="positions-page">
      <SEOHead
        title="CDL Truck Driver Job Openings | OTR, Flatbed, Reefer, Lease"
        description="Open CDL-A truck driver positions at Hylas Companion: Company OTR (85¢+ CPM), Team Driving ($1 CPM), Lease Purchase (88%), Local W2 up to $2,500/wk. Hiring in Chicago, Denver, Oklahoma, Baltimore, and all 48 states."
        keywords="CDL-A truck driver jobs, OTR driver openings, flatbed trucking jobs, reefer driver jobs, lease purchase truck driver, local truck driver jobs, company driver CPM, team driving jobs"
        path="/positions"
        schema={POSITIONS_SCHEMA}
      />
      <section className="pt-36 md:pt-44 pb-12 max-w-7xl mx-auto px-6 md:px-12">
        <Overline>Positions</Overline>
        <h1 className="font-head text-4xl md:text-6xl font-bold uppercase tracking-[0.15em] mt-5 max-w-4xl leading-tight">
          Find Your Seat
        </h1>
        <p className="mt-6 text-lg text-foreground/70 max-w-2xl">
          Nine pay packages across four position categories. Every one comes with new trucks, top pay, and flexible home time. Dedicated lanes running Chicago, IL · Denver, CO · Baltimore, MD · Oklahoma and all 48 states.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 space-y-6">
        {POSITIONS.map((p, i) => {
          const relatedPkgs = (POSITION_PACKAGES[p.key] || [])
            .map((id) => PAY_PACKAGES.find((pkg) => pkg.id === id))
            .filter(Boolean);

          return (
            <div key={p.key} data-testid={`position-${p.key}`} className="border border-border bg-white p-8 md:p-12">
              <div className="grid lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1">
                  <span className="font-head text-xs tracking-[0.3em] text-primary">0{i + 1}</span>
                  <h2 className="font-head text-3xl md:text-4xl uppercase tracking-[0.1em] mt-4 text-[#0A0A0A]">{p.title}</h2>
                  <Link
                    to="/apply"
                    data-testid={`apply-${p.key}`}
                    className="mt-6 inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] bg-primary text-black px-7 py-4 hover:bg-[#0A0A0A] hover:text-primary transition-colors duration-300"
                  >
                    Apply For This <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="lg:col-span-2">
                  <p className="text-lg text-foreground/75 leading-relaxed">{p.blurb}</p>
                  <div className="grid sm:grid-cols-2 gap-3 mt-6">
                    {p.points.map((pt) => (
                      <div key={pt} className="flex items-center gap-3 text-foreground/80">
                        <Check className="w-5 h-5 text-primary shrink-0" /> {pt}
                      </div>
                    ))}
                  </div>

                  {/* Related pay packages */}
                  {relatedPkgs.length > 0 && (
                    <div className="mt-8">
                      <p className="font-head text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Pay Packages</p>
                      <div className="flex flex-wrap gap-2">
                        {relatedPkgs.map((pkg) => (
                          <div key={pkg.id} className="border border-border px-4 py-2 flex flex-col">
                            <span className="font-head text-lg font-bold text-[#0A0A0A]">{pkg.label}</span>
                            <span className="font-head text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{pkg.title}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <LocalPositionsMap />

      {/* Driver Transfer Market cross-link — creates inbound link for Googlebot */}
      <section className="py-16 bg-[#080808] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <span className="font-head text-[9px] uppercase tracking-[0.4em] text-primary/60">For Carriers</span>
            <h2 className="font-head text-2xl md:text-3xl font-bold uppercase tracking-[0.08em] text-white mt-2">
              Browse the Driver Transfer Market
            </h2>
            <p className="mt-3 text-white/50 text-sm max-w-lg">
              Carriers bid on pre-screened CDL-A drivers across 10 states. Transparent placement fees,
              verified documents, and direct contact after a winning bid.
            </p>
          </div>
          <Link
            to="/market"
            className="shrink-0 inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] bg-primary text-black px-8 py-4 hover:brightness-110 transition-all"
          >
            Open the Market <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
